/**
 * Outline Heading
 *
 * PHP renders the finished markup; this file does one job — measure the text and
 * write the SVG's viewBox, plus its pixel size in font-size mode. Everything
 * else (colours, stroke width, typography) is CSS driven by Bricks controls.
 *
 * Note: gulp wraps this bundle in an IIFE, so anything that must be reachable
 * from Bricks (the `$scripts` property) has to be assigned onto `window`.
 */

var MOH_SELECTOR = ".mad-outline-heading";

// 9-way alignment → SVG preserveAspectRatio. Mirrors $align_map in the PHP.
var MOH_ALIGN = {
    "top-left": "xMinYMin",
    "top-center": "xMidYMin",
    "top-right": "xMaxYMin",

    "center-left": "xMinYMid",
    center: "xMidYMid",
    "center-right": "xMaxYMid",

    "bottom-left": "xMinYMax",
    "bottom-center": "xMidYMax",
    "bottom-right": "xMaxYMax"
};

// Elements that already carry their observers, so a repeat init only re-measures.
var mohReady = new WeakSet();

// One shared 2D context, used only for measureText().
var mohCanvas = null;

function madOutlineHeadingTransform(value, transform) {
    if (transform === "uppercase") {
        return value.toUpperCase();
    }

    if (transform === "lowercase") {
        return value.toLowerCase();
    }

    if (transform === "capitalize") {
        return value.replace(/(^|\s)(\S)/g, function (m, space, letter) {
            return space + letter.toUpperCase();
        });
    }

    return value;
}

/**
 * The box to fit, in the text's user units.
 *
 * getBBox() is no good for this on its own: it returns the *font* box — the
 * font's full ascent + descent, and each glyph's advance including side
 * bearings. For "BENEFIT" in Avenir that is ~20 % empty space above the capitals
 * (room for accents), ~20 % below the baseline (room for descenders) and a few
 * units left of the B, and all of it scales up with the text. line-height cannot
 * help, because SVG <text> has no line box.
 *
 * So the ink is measured with canvas measureText(), which reports the actual
 * glyph extents for the same font:
 *
 *   vertical    from the whole string (letter-spacing does not affect it)
 *   horizontal  from the first and last glyph only, positioned with
 *               getExtentOfChar() — so letter-spacing and kerning come from the
 *               SVG layout itself and never have to be replicated in canvas
 *
 * data-trim picks what to trim to:
 *   glyphs  the ink on all four sides (default)
 *   cap     cap height → baseline, horizontal ink. Consistent across words with
 *           and without ascenders/descenders; descenders hang out of the box.
 *   font    the raw font box, i.e. getBBox()
 *
 * Neither box includes the stroke, so the SCSS lets the SVG overflow.
 */
function madOutlineHeadingMeasure(root, text) {
    // Throws when the node is not rendered (display:none, detached from the DOM).
    var bbox = text.getBBox();
    var trim = root.getAttribute("data-trim") || "glyphs";
    var count = text.getNumberOfChars();

    if (trim === "font" || !count) {
        return bbox;
    }

    if (!mohCanvas) {
        mohCanvas = document.createElement("canvas").getContext("2d");
    }

    if (!mohCanvas || typeof mohCanvas.measureText !== "function") {
        return bbox;
    }

    var style = window.getComputedStyle(text);

    mohCanvas.font = [
        style.fontStyle,
        style.fontWeight,
        style.fontSize,
        style.fontFamily
    ].join(" ");

    var value = madOutlineHeadingTransform(text.textContent, style.textTransform);
    var last = Math.min(value.length, count) - 1;

    var all = mohCanvas.measureText(value);
    var firstGlyph = mohCanvas.measureText(value.charAt(0));
    var lastGlyph = mohCanvas.measureText(value.charAt(last));

    // Older engines without the actualBoundingBox* metrics.
    if (typeof all.actualBoundingBoxAscent !== "number") {
        return bbox;
    }

    var x1 = text.getExtentOfChar(0).x - firstGlyph.actualBoundingBoxLeft;
    var x2 = text.getExtentOfChar(last).x + lastGlyph.actualBoundingBoxRight;

    var y1 = -all.actualBoundingBoxAscent;
    var y2 = all.actualBoundingBoxDescent;

    if (trim === "cap") {
        y1 = -mohCanvas.measureText("H").actualBoundingBoxAscent;
        y2 = 0;
    }

    return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

/**
 * Measure the text and size the SVG around it.
 *
 * The viewBox is the measured box, which is what lets "Scale to fit" hand the
 * scaling to the SVG itself.
 */
function madOutlineHeadingUpdate(root) {
    var svg = root.querySelector(".mad-outline-heading__svg");
    var text = root.querySelector(".mad-outline-heading__text");

    if (!svg || !text) {
        return;
    }

    var bbox;

    try {
        bbox = madOutlineHeadingMeasure(root, text);
    } catch (e) {
        return;
    }

    if (!(bbox.width > 0) || !(bbox.height > 0)) {
        return;
    }

    svg.setAttribute(
        "viewBox",
        [bbox.x, bbox.y, bbox.width, bbox.height].join(" ")
    );

    if (root.getAttribute("data-sizing") === "font-size") {
        // Size the SVG to the text so it renders 1:1; the root's flexbox places it.
        svg.style.width = bbox.width + "px";
        svg.style.height = bbox.height + "px";
        svg.removeAttribute("preserveAspectRatio");

        return;
    }

    // Scale to fit: the SVG fills the box and scales the viewBox into it.
    svg.style.removeProperty("width");
    svg.style.removeProperty("height");

    var align = root.getAttribute("data-align");

    svg.setAttribute(
        "preserveAspectRatio",
        (MOH_ALIGN[align] || "xMidYMid") + " meet"
    );
}

/**
 * Attach the observers once, then measure. Safe to call repeatedly — Bricks
 * re-invokes the init on every builder re-render.
 */
function madOutlineHeadingSetup(root) {
    if (mohReady.has(root)) {
        madOutlineHeadingUpdate(root);
        return;
    }

    mohReady.add(root);

    // Keeps "Scale to fit" correct on resize and while dragging in the builder.
    if (window.ResizeObserver) {
        new ResizeObserver(function () {
            madOutlineHeadingUpdate(root);
        }).observe(root);
    }

    new MutationObserver(function () {
        madOutlineHeadingUpdate(root);
    }).observe(root, {
        attributes: true,
        attributeFilter: ["data-sizing", "data-align", "data-trim"]
    });

    madOutlineHeadingUpdate(root);
}

function madOutlineHeadingScan() {
    var elements = document.querySelectorAll(MOH_SELECTOR);

    Array.prototype.forEach.call(elements, madOutlineHeadingSetup);
}

function initMadOutlineHeading() {
    // Paint immediately rather than waiting on fonts …
    madOutlineHeadingScan();

    // … then measure again once the real font is in, since until then both the
    // SVG layout and canvas report the fallback font's metrics.
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(madOutlineHeadingScan);
    }
}

// Exposed for Bricks' `$scripts` property, which also re-invokes it after every
// builder re-render — no extra MutationObserver on the document is needed.
window.initMadOutlineHeading = initMadOutlineHeading;

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMadOutlineHeading);
} else {
    initMadOutlineHeading();
}
